/**
 * Shared mock hall-pass vocabulary + availability logic.
 *
 * Extracted from PointsActionBarDock so the /points hall-pass popover and
 * the Hall Pass Kiosk compute identical states from identical data. Until
 * Pass Types are backed by the API this is the source vocabulary for both
 * surfaces (colors stay derived from names via lib/passColors).
 */

export interface HallPassTypeConfig {
  name: string;
  /** Mock daily limit per student. */
  limit: number;
  /** Time limit every pass of this type starts with, in minutes. */
  maxDurationMin: number;
  /**
   * Kiosk behavior: self-start types activate immediately ("Start Pass");
   * the rest go through a mock approval ("Request Pass"). The /points
   * popover ignores this: staff-issued passes are always immediate.
   */
  selfStart: boolean;
}

// Available hall-pass destinations and their mock daily limits.
// Time limits mirror the Pass Types page defaults (see hall-passes.pass-types)
// so every surface quotes the same numbers.
export const HALL_PASS_TYPES: HallPassTypeConfig[] = [
  { name: 'Bathroom', limit: 3, maxDurationMin: 5, selfStart: true },
  { name: 'Nurse', limit: 2, maxDurationMin: 15, selfStart: false },
  { name: 'Library', limit: 2, maxDurationMin: 15, selfStart: true },
  { name: 'Counselor', limit: 1, maxDurationMin: 20, selfStart: false },
  { name: 'Main Office', limit: 2, maxDurationMin: 10, selfStart: false },
  { name: 'Gym', limit: 1, maxDurationMin: 10, selfStart: true },
];

/**
 * The 1-2 pass types this student has active limits on. Deterministic per
 * student but varied across students, so demoing different students shows
 * different limit mixes instead of every type carrying a "N of Y today" line.
 *
 * Sensitive/health destinations (Nurse, Counselor) are never limited — a
 * student can always go, so those rows stay plainly available.
 */
const NEVER_LIMITED = new Set(['Nurse', 'Counselor']);

export function mockActiveLimitTypes(studentId: string): Set<string> {
  const eligible = HALL_PASS_TYPES.filter((t) => !NEVER_LIMITED.has(t.name));
  const key = `${studentId}|limits`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const h = Math.abs(hash);
  const first = h % eligible.length;
  const types = new Set<string>([eligible[first].name]);
  // ~2/3 of students carry a second limit.
  if (h % 3 !== 0) {
    const step = 1 + ((h >> 3) % (eligible.length - 1));
    types.add(eligible[(first + step) % eligible.length].name);
  }
  return types;
}

/** Stable pseudo-count (0..limit) of passes a student has taken of a type today. */
export function mockPassesTakenToday(studentId: string, passType: string, limit: number): number {
  const key = `${studentId}:${passType}`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % (limit + 1);
}

/**
 * Stable mock reason a student is ineligible for a pass type, or undefined if
 * available. Reaching the daily limit takes priority; otherwise a couple of
 * other reasons are sprinkled deterministically so the blocked state is
 * demoable (capacity, a conflicting peer being out). Shown on the amber
 * reason line of the popover row, so keep these short and scannable; the
 * limit reason folds in the count that would otherwise be lost.
 */
export function mockPassUnavailableReason(
  studentId: string,
  passType: string,
  taken: number,
  limit: number,
): string | undefined {
  if (taken >= limit) return `Daily limit reached (${taken} of ${limit})`;
  const key = `${studentId}|why|${passType}`;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  const h = Math.abs(hash);
  if (h % 7 === 0) return 'At capacity right now';
  if (h % 5 === 0) return 'Someone in a No Meetup group is out';
  return undefined;
}

/**
 * Where a student may start this pass type for themselves (ENG-9904).
 *
 * A school running kiosks at the door often wants the kiosk and ONLY the
 * kiosk: a pass you take by walking up to the screen in the room, not one you
 * can start from your phone anywhere in the building.
 *
 * ## The two channels are different endpoints, which is what makes this work
 *
 * The kiosk creates through the STAFF endpoint, inside the launching teacher's
 * session — campus sees a staff-issued pass and never consults `selfStart`.
 * The portal (and the student app, which is the same portal in a Capacitor
 * shell) creates through `POST /hallpass/<school>/self`, which is the only
 * place campus has any reason to look at it.
 *
 * So `selfStart` is read as "may a student start this FROM THE APP", and the
 * kiosk gets its own key. That direction was chosen deliberately over the
 * obvious alternative of keeping `selfStart: true` for both on-states and
 * adding a channel field:
 *
 *  - It is BACKWARDS COMPATIBLE BY CONSTRUCTION. Every `selfStart: true` in
 *    production already means "kiosk and app", and every `false` or absent one
 *    means off. No migration, no backfill.
 *  - It puts the app block on the right side of the app-store problem. The
 *    student app ships on Apple's schedule, so a gate that lives only in
 *    client code keeps offering the pass on every install that has not
 *    updated. Turning `selfStart` off means an old app asks campus and campus
 *    is the one saying no.
 *
 * ⚠️ THAT SECOND POINT IS UNVERIFIED. Campus is *believed* to refuse a
 * self-start when `selfStart` is off (there is a `self_start_not_allowed` deny
 * reason for it), but the deny enum has been wrong before: `destination_sensitive`
 * exists and campus still answers 201 to a sensitive self-start, probed live on
 * gamma (see below). Until somebody probes `selfStart: false` the same way,
 * treat the app block as CLIENT-SIDE — which is what `can_issue` already is.
 * Nothing here depends on the answer; it only changes what we may promise.
 */
export type SelfStartMode = 'off' | 'kiosk' | 'both';

/** Metadata key carrying kiosk-only self-start. Absent on every existing type. */
export const KIOSK_SELF_START_KEY = 'kioskSelfStart';

/**
 * Read a pass type's self-start mode.
 *
 * `both` whenever `selfStart` is on, whatever the kiosk key says: an existing
 * type has no kiosk key at all, and a school that turns the app back on should
 * not have to know a second flag was left behind.
 */
export function selfStartMode(dest: {
  metadata?: Record<string, unknown> | null;
}): SelfStartMode {
  const m = dest.metadata ?? {};
  if (m.selfStart === true) return 'both';
  return m[KIOSK_SELF_START_KEY] === true ? 'kiosk' : 'off';
}

/**
 * The metadata a chosen mode writes.
 *
 * Both keys, always, so a type cannot end up with a stale `kioskSelfStart`
 * contradicting its `selfStart` — the reason `selfStartMode` has to prefer one
 * over the other when reading.
 */
export function selfStartMetadata(mode: SelfStartMode): {
  selfStart: boolean;
  [KIOSK_SELF_START_KEY]: boolean;
} {
  return {
    selfStart: mode === 'both',
    [KIOSK_SELF_START_KEY]: mode === 'kiosk' || mode === 'both',
  };
}

/**
 * Archived types are startable nowhere, whatever their mode says.
 *
 * Sensitive is deliberately NOT excluded (ENG-9593, ENG-9711). §3 lists these
 * pickers as places that show a Sensitive type's real name, "because picking a
 * destination requires seeing it". The portal used to exclude Sensitive on the
 * theory that campus "means to refuse" a student-issued one — it does not:
 * `POST /hallpass/<school>/self` with a Sensitive destination answers 201 with
 * an active pass (probed live on gamma). Excluding it hid self-start types a
 * school had deliberately turned on, and made HP-409 untestable from the kiosk.
 *
 * Eligibility, limits and capacity aren't reliably known client-side; the
 * create 409 carries the real deny reason.
 */
function isStartableAtAll(dest: { metadata?: Record<string, unknown> | null }): boolean {
  return (dest.metadata ?? {}).archived !== true;
}

/** May a student start this from a KIOSK? True for both on-states. */
export function isKioskSelfStartable(dest: {
  metadata?: Record<string, unknown> | null;
}): boolean {
  return selfStartMode(dest) !== 'off' && isStartableAtAll(dest);
}

/** May a student start this from the portal or the student app? */
export function isAppSelfStartable(dest: {
  metadata?: Record<string, unknown> | null;
}): boolean {
  return selfStartMode(dest) === 'both' && isStartableAtAll(dest);
}
